import type { DecisionOption } from '@deskworks/shared-types';
import { Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/**
 * The answers an agent offers on an approval or a question (docs/features/agent-mode-and-approvals.md#options): one
 * button each, its recommendation first and marked. A click is the decision.
 */
export function DecisionOptions({
  options,
  onPick,
  picking,
  disabled,
  className,
}: {
  options: DecisionOption[];
  onPick: (label: string) => void;
  /** The label being sent right now. */
  picking?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const sorted = [...options].sort((a, b) => Number(!!b.recommended) - Number(!!a.recommended));
  return (
    <div className={cn('grid gap-2 sm:grid-cols-2', className)}>
      {sorted.map((o) => (
        <button
          key={o.label}
          type="button"
          disabled={disabled || !!picking}
          onClick={() => onPick(o.label)}
          className={cn(
            'flex min-w-0 cursor-pointer flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default disabled:opacity-60',
            o.recommended
              ? 'border-emerald-500 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:hover:bg-emerald-950/70'
              : 'bg-card hover:bg-accent',
          )}
        >
          <span className="flex w-full items-start gap-1.5 font-medium break-words">
            {picking === o.label && <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin" />}
            <span className="min-w-0 flex-1">{o.label}</span>
          </span>
          {o.detail && <span className="text-xs break-words text-muted-foreground">{o.detail}</span>}
          {o.recommended && (
            <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              <Sparkles className="size-3" /> {t('Recommended')}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/** The answers a question offers: a click answers it and the work goes on. */
export function QuestionOptions({
  workItemId,
  options,
  onAnswered,
  className,
}: {
  workItemId: string;
  options: DecisionOption[];
  onAnswered?: () => void;
  className?: string;
}) {
  const toast = useToast();
  const [picking, setPicking] = useState<string | null>(null);
  const pick = async (answer: string) => {
    setPicking(answer);
    try {
      await api.workItems.ready(workItemId, { answer });
      onAnswered?.();
    } catch (err) {
      toast.error(t('That didn’t work'), errorMessage(err));
    } finally {
      setPicking(null);
    }
  };
  return <DecisionOptions className={className} options={options} picking={picking} onPick={(label) => void pick(label)} />;
}
